import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { ModelMapperService } from 'src/app/services/util/model.mapper.service';
import { StorageService } from 'src/app/services/util/storage.service';
import { DynamicTranslations } from 'src/locale/dynamicTranslations';
import { IBalance } from '../models/balance.interface';
import { IBankDataRequest } from '../models/requests/bankDataRequest.interface';
import { ITransferBalanceAmountRequest } from '../models/requests/transferBalanceAmountRequest.interface';
import { IResolveAccountNumberResponseData } from '../models/response/resolveAccountNumberResponseData.interface';
import { IUnregisteredAffiliateResponse } from '../models/response/unregisteredAffiliateResponse.interface';
import { IAffiliate } from '../models/user/affiliate.interface';
import { IAffiliateSessionsWithPages } from '../models/user/affiliateSessionsWithPages.interface';
import { IBank } from '../models/user/bank.interface';
import { ICreateOrUpdateAffiliate } from '../models/user/createOrUpdateAffiliate.interface';
import { IRegistrationConfirm } from '../models/user/registrartionConfirm';
import { IAffiliateSessionTO } from '../models/user/transportedObjects/affiliateSessionTO.interface';
import { AffiliateRole, TransactionType } from '../models/util/tmEnums';
import { AuthenticationService } from './authentication.service';
import { ModelMapperTMService } from './model-mapper-tm.service';
import { AffiliateProviderService } from './providers/affiliate.provider.service';
import { ICustomNotification } from 'src/app/models/notification/customNotification.interface';
import { NotificationService } from 'src/app/services/notification.service';
import { ErrorLevel, ErrorType } from 'src/app/models/enums';
import { LoaderService } from 'src/app/services/loader.service';
import { ViewConfigurations } from 'src/app/models/util/viewConfigurations';
import { IAffiliateSetting } from '../models/settings/affiliateSetting.interface';
import { IAffiliateSettingDA } from '../models/settings/DAObjects/affiliateSettingDA.interface';

@Injectable({
  providedIn: 'root'
})
export class AffiliateService {
  loggedAffiliateSubAffiliates?: IAffiliate[];
  affiliatesByShopId: {[shopId: number]: IAffiliate[]} = {};
  loggedAffiliateId: number;
  banks: IBank[];
  affiliateSettings: IAffiliateSetting[];
  private loggedAffiliateSubAffiliatesChangedSource = new Subject<void>();
  public loggedAffiliateSubAffiliatesChangedSource$ = this.loggedAffiliateSubAffiliatesChangedSource.asObservable();

  constructor(
    private affiliateProvider: AffiliateProviderService,
    private authService: AuthenticationService,
    private modelMapper: ModelMapperTMService,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private loaderService: LoaderService) {

    if (this.authService.loggedAffiliate) {
      this.affiliateProvider.getAffiliateBalanceByAffiliateId(this.storageService.affiliate.affiliateId).subscribe(x => {
        // this.storageService.accountBalance = x?.amount;
        // if (this.authService.loggedAffiliate?.currentBalance) {
        //   this.authService.loggedAffiliate.currentBalance = x?.amount;
        // }
        // this.storageService.affiliate = this.authService.loggedAffiliate;
      });
    }
  }

  getAllSubAffiliatesForAffiliate(affiliateId: number, onlyByParentId: boolean, loadFromServer: boolean = false): Observable<IAffiliate[]> {
    if (this.loggedAffiliateId !== this.authService.loggedAffiliate.affiliateId) {
      this.loggedAffiliateSubAffiliates = null;
      this.affiliatesByShopId = {};
      this.loggedAffiliateId = this.authService.loggedAffiliate.affiliateId;
    }

    if ((affiliateId === this.loggedAffiliateId || (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager && affiliateId === this.authService.loggedAffiliate.parentId)) && this.loggedAffiliateSubAffiliates && !loadFromServer) {
      if (onlyByParentId) {
        return of(this.loggedAffiliateSubAffiliates.filter(aff => aff.parentId === affiliateId));
      } else {
        return of(this.loggedAffiliateSubAffiliates);
      }
    }

    if (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.cashier) {
      return of(null);
    }

    return this.affiliateProvider.getAllAffiliatesByParentId(affiliateId).pipe(
      switchMap((affiliateListTO) => {
        if (affiliateListTO) {
          // const affiliates = affiliateListTO.map(affiliateTO => this.modelMapper.mapAffiliate(
          //   affiliateTO,
          //   affiliateTO.parentId === this.authService.loggedAffiliate.affiliateId ? this.authService.loggedAffiliate.firstName + ' ' + this.authService.loggedAffiliate.lastName : ''));
          // if (affiliateId === this.authService.loggedAffiliate.affiliateId) {
          //   this.loggedAffiliateSubAffiliates = affiliates.filter(aff => aff.affiliateId !== affiliateId);
          // }
          this.loggedAffiliateSubAffiliates = [];
          this.affiliatesByShopId = {};
          const allAff: IAffiliate[] = [];
          for (const afiliateTo of affiliateListTO) {
            const affiliate = this.modelMapper.mapAffiliate(
              afiliateTo,
              afiliateTo.parentId === this.authService.loggedAffiliate.affiliateId ? this.authService.loggedAffiliate.firstName + ' ' + this.authService.loggedAffiliate.lastName : '');
            allAff.push(affiliate);
            if (affiliate.affiliateId === affiliateId) {
              continue;
            }
            if (affiliate.affiliateRole === AffiliateRole.cashier && !affiliate.workInShopId) {
              continue;
            }
            if (
              (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.superAgent || this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager)
              && affiliate.workInShopId
              && ViewConfigurations.isShopAndCashierCreateDiabledToAgent) {
              if (!this.affiliatesByShopId[affiliate.workInShopId]) {
                this.affiliatesByShopId[affiliate.workInShopId] = [];
              }

              this.affiliatesByShopId[affiliate.workInShopId].push(affiliate);
            }
            this.loggedAffiliateSubAffiliates.push(affiliate);
          }

          for (const aff of allAff) {
            aff.subAffiliates = allAff.filter(a => a.parentId === aff.affiliateId);
          }

          if (onlyByParentId) {
            return of(this.loggedAffiliateSubAffiliates.filter(aff => aff.parentId === affiliateId));
          } else {
            return of(this.loggedAffiliateSubAffiliates);
          }
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getAllAffiliatesByShopId(shopId: number, shopOwnerAffiliate: IAffiliate): Observable<IAffiliate[]> {
    if (!shopOwnerAffiliate) {
      return this.getAllSubAffiliatesForAffiliate(this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager ?  this.authService.loggedAffiliate.parentId : this.authService.loggedAffiliate.affiliateId, false).pipe(
        switchMap(affiliates =>
          of(affiliates.filter(aff => aff.workInShopId === shopId))
        )
      );
    } else {
      return this.getAllAffiliatesForShop(shopId, shopOwnerAffiliate.firstName + ' ' + shopOwnerAffiliate.lastName);
    }
  }

  getAffiliateByAffiliateIdFromShop(affiliateId: number, shopId: number, parentName: string): Observable<IAffiliate> {
    if (this.affiliatesByShopId[shopId]) {
      return of(this.affiliatesByShopId[shopId].find(aff => aff.affiliateId === affiliateId));
    } else {
      return this.getAllAffiliatesForShop(shopId, parentName).pipe(
        switchMap(affiliates =>
          of(affiliates.find(aff => aff.affiliateId === affiliateId))
        ));
    }
  }

  private getAllAffiliatesForShop(shopId: number, parentName: string): Observable<IAffiliate[]> {

    if (this.affiliatesByShopId[shopId]) {
      return of(this.affiliatesByShopId[shopId]);
    }

    return this.affiliateProvider.getAllAffiliatesByShopId(shopId).pipe(
      switchMap((response) => {
        if (response) {
          this.affiliatesByShopId[shopId] = response.map(affTo => this.modelMapper.mapAffiliate(affTo, parentName));

          return of(this.affiliatesByShopId[shopId]);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }


  registerNewAffiliate(form: any): Observable<boolean> {
    const affiliate = this.createAffiliateFromForm(form, false, false);
    const password = form.value.matchingPasswords.password;
    return this.affiliateProvider.createAffiliate({ password, affiliate: this.modelMapper.mapAffiliateTO(affiliate) }).pipe(
      switchMap((response) => {
        if (response) {
          if (ViewConfigurations.isShopAndCashierCreateDiabledToAgent && affiliate.affiliateRole === AffiliateRole.cashier) {
            const notification = {
              title: DynamicTranslations.notification_title,
              message: 'Cashier successfully created.',
              errorLevel: ErrorLevel.Success,
              errorType: ErrorType.CloseDialog,
            };
            this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          } else {
            const notification = {
              title: DynamicTranslations.notification_title,
              message: DynamicTranslations.ticketManager_RegistrationEmailSent,
              errorLevel: ErrorLevel.Success,
              errorType: ErrorType.CloseDialog,
            };
            this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          }
          return of(response);
        }
        // alert('error');
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  confirmRegistration(form: any, token: string, accountNumber: string, externalBankIdentifier: string): Observable<boolean> {
    const registrationConfirm: IRegistrationConfirm = {
      username: form.value.username,
      newPassword: form.value.matchingPasswords.password,
      registrationToken: token,
      accountNumber,
      externalBankIdentifier
    };
    return this.affiliateProvider.continueRegistration(registrationConfirm).pipe(
      switchMap((response) => {
        if (response) {
          // auto login user after finish registration
          // this.authService.login(registrationConfirm.username, registrationConfirm.newPassword).subscribe();
          return of(true);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  updateAffiliate(form: any, idAffilate: number, isUpdateSubAffiliate: boolean, isDeleted: boolean, worksInShopId: number, isUsernameChanged: boolean): Observable<boolean> {
    const affiliate = this.createAffiliateFromForm(form, isDeleted, isUsernameChanged);
    affiliate.affiliateId = idAffilate;
    return this.affiliateProvider.updateAffiliate(this.modelMapper.mapAffiliateTO(affiliate)).pipe(
      switchMap((response) => {
        if (response) {
          if (!isUpdateSubAffiliate) {
            // if logged user change personal data
            this.authService.updateUserData(affiliate);
          } else {
            if (worksInShopId && this.authService.loggedAffiliate.affiliateRole !== AffiliateRole.agent) {

              const index = this.affiliatesByShopId[worksInShopId]?.findIndex(aff => aff.affiliateId === affiliate.affiliateId);
              // this.loggedAffiliateSubAffiliates[index] = affiliate;
              this.affiliatesByShopId[worksInShopId][index].username = affiliate.username;
              this.affiliatesByShopId[worksInShopId][index].email = affiliate.email;
              this.affiliatesByShopId[worksInShopId][index].firstName = affiliate.firstName;
              this.affiliatesByShopId[worksInShopId][index].lastName = affiliate.lastName;
              this.affiliatesByShopId[worksInShopId][index].identityNumber = affiliate.identityNumber;
              this.affiliatesByShopId[worksInShopId][index].birthDate = affiliate.birthDate;
              this.affiliatesByShopId[worksInShopId][index].address = affiliate.address;
              this.affiliatesByShopId[worksInShopId][index].city = affiliate.city;
              this.affiliatesByShopId[worksInShopId][index].state = affiliate.state;
              this.affiliatesByShopId[worksInShopId][index].phone = affiliate.phone;
              this.affiliatesByShopId[worksInShopId][index].gender = affiliate.gender;
              this.affiliatesByShopId[worksInShopId][index].workInShopId = affiliate.workInShopId ? affiliate.workInShopId : this.affiliatesByShopId[worksInShopId][index].workInShopId;
              this.affiliatesByShopId[worksInShopId][index].aCommissionSettingId = affiliate.aCommissionSettingId;
              this.affiliatesByShopId[worksInShopId][index].aBonusSettingId = affiliate.aBonusSettingId;
              this.affiliatesByShopId[worksInShopId][index].aPowerBonusSettingId = affiliate.aPowerBonusSettingId;
              this.affiliatesByShopId[worksInShopId][index].affiliateSettingId = affiliate.affiliateSettingId;

              if (worksInShopId !== this.affiliatesByShopId[worksInShopId][index].workInShopId && this.affiliatesByShopId[worksInShopId][index].workInShopId) {
                if (!this.affiliatesByShopId[this.affiliatesByShopId[worksInShopId][index].workInShopId]) {
                  this.affiliatesByShopId[this.affiliatesByShopId[worksInShopId][index].workInShopId] = [];
                }
                this.affiliatesByShopId[this.affiliatesByShopId[worksInShopId][index].workInShopId].push(this.affiliatesByShopId[worksInShopId][index]);
                this.affiliatesByShopId[worksInShopId] = this.affiliatesByShopId[worksInShopId].filter(aff => aff.workInShopId === worksInShopId);
              }
            } else {

              // if logged user change  data of his subAffiliates
              const index = this.loggedAffiliateSubAffiliates.findIndex(aff => aff.affiliateId === affiliate.affiliateId);
              // this.loggedAffiliateSubAffiliates[index] = affiliate;
              this.loggedAffiliateSubAffiliates[index].username = affiliate.username;
              this.loggedAffiliateSubAffiliates[index].email = affiliate.email;
              this.loggedAffiliateSubAffiliates[index].firstName = affiliate.firstName;
              this.loggedAffiliateSubAffiliates[index].lastName = affiliate.lastName;
              this.loggedAffiliateSubAffiliates[index].identityNumber = affiliate.identityNumber;
              this.loggedAffiliateSubAffiliates[index].birthDate = affiliate.birthDate;
              this.loggedAffiliateSubAffiliates[index].address = affiliate.address;
              this.loggedAffiliateSubAffiliates[index].city = affiliate.city;
              this.loggedAffiliateSubAffiliates[index].state = affiliate.state;
              this.loggedAffiliateSubAffiliates[index].phone = affiliate.phone;
              this.loggedAffiliateSubAffiliates[index].gender = affiliate.gender;
              this.loggedAffiliateSubAffiliates[index].workInShopId = affiliate.workInShopId ? affiliate.workInShopId : this.loggedAffiliateSubAffiliates[index].workInShopId;
              this.loggedAffiliateSubAffiliates[index].aCommissionSettingId = affiliate.aCommissionSettingId;
              this.loggedAffiliateSubAffiliates[index].aBonusSettingId = affiliate.aBonusSettingId;
              this.loggedAffiliateSubAffiliates[index].aPowerBonusSettingId = affiliate.aPowerBonusSettingId;
              this.loggedAffiliateSubAffiliates[index].affiliateSettingId = affiliate.affiliateSettingId;
            }
          }

          const notification = {
            title: DynamicTranslations.notification_title,
            message: DynamicTranslations.ticketManager_Affiliate + ' - ' + DynamicTranslations.notification_updated,
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);

          return of(response);
        }
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return of(false);
      }),
    );
  }

  getAffiliateBalanceByAffiliateId(id: number): Observable<IBalance> {
    return this.affiliateProvider.getAffiliateBalanceByAffiliateId(id).pipe(
      switchMap((response) => {
        if (response) {

          this.storageService.accountBalance = response?.amount;
          if (this.authService.loggedAffiliate && this.authService.loggedAffiliate.affiliateId === id) {
            this.authService.loggedAffiliate.currentBalance = response?.amount;
          }
          this.storageService.affiliate = this.authService.loggedAffiliate;
          return of(response as IBalance);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getAffiliateByAffiliateId(id: number): Observable<IAffiliate> {
    if (this.loggedAffiliateSubAffiliates) {
      return of(this.loggedAffiliateSubAffiliates.find(aff => aff.affiliateId === id));
    } else {
      return this.getAllSubAffiliatesForAffiliate(this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager ?  this.authService.loggedAffiliate.parentId : this.authService.loggedAffiliate.affiliateId, false).pipe(
        switchMap(affiliates =>
          of(affiliates.find(aff => aff.affiliateId === id))
        ));
    }
  }

  createAffiliateFromForm(form: any, isDeleted: boolean, isUsernameChanged: boolean): IAffiliate {
    const affiliate: IAffiliate = {
      affiliateId: 0,
      firstName: form?.value?.firstName,
      lastName: form?.value?.lastName,
      parentId: form?.value?.agent?.affiliateId ? form?.value?.agent?.affiliateId : (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agentManager ?  this.authService.loggedAffiliate.parentId : this.authService.loggedAffiliate.affiliateId),
      // hierarchy: null,
      // depth: null,
      email: form?.value?.email,
      address: form?.value?.address,
      phone: form?.value?.phone,
      isDeleted,
      dateCreated: null,
      dateUpdated: null,
      birthDate: form?.value?.birthDate !== '' ? new Date(form?.value?.birthDate) : null,
      affiliateRole: form?.value?.affiliateRole,
      identityNumber: form?.value?.identityNumber,
      city: form?.value?.city,
      state: form?.value?.state,
      gender: form?.value?.gender,
      username: form?.value?.username ? form?.value?.username : form?.controls?.username?.value ? form?.controls?.username?.value : null,
      workInShopId: form?.value?.shop?.id ? form?.value?.shop?.id : null,
      shops: form?.value?.shops?.length > 0 ? form?.value?.shops : null,
      currentBalance: form?.value?.currentBalance ? form?.value?.currentBalance : null,
      aCommissionSettingId: form?.value?.aCommissionSettingId ? form?.value?.aCommissionSettingId : null,
      aBonusSettingId: form?.value?.aBonusSettingId ? form?.value?.aBonusSettingId : null,
      aPowerBonusSettingId: form?.value?.aPowerBonusSettingId ? form?.value?.aPowerBonusSettingId : null,
      parentName: this.authService.loggedAffiliate.firstName + ' ' + this.authService.loggedAffiliate.lastName,
      affiliateSettingId: form?.value?.affiliateSettingId ? form?.value?.affiliateSettingId : null,
      subAffiliates: [],
      isUsernameChanged
    };
    return affiliate;

  }

  getRoleNameFromEnum(affiliateRole: AffiliateRole): string {
    switch (affiliateRole) {
      case AffiliateRole.agent: {
        return 'Agent';
      }
      case AffiliateRole.superAgent: {
        return 'Super Agent';
      }
      case AffiliateRole.agentManager: {
        return 'Agent Manager';
      }
      case AffiliateRole.masterAgent: {
        return 'Master Agent';
      }
      case AffiliateRole.operatorTrader: {
        return 'Operator Trader';
      }
      case AffiliateRole.cashier: {
        return 'Cashier';
      }
    }
  }

  getAllBanks(): Observable<IBank[]> {
    if (!ViewConfigurations.isPaystackEnabled) {
      return of([]);
    }
    if (this.banks) {
      return of(this.banks);
    }
    return this.affiliateProvider.getAllBanks().pipe(
      switchMap((banksTO) => {
        if (banksTO) {
          const banks = banksTO.map(bankTO => this.modelMapper.mapBank(bankTO));
          this.banks = banks;
          return of(banks);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  validateBankAccount(accountNumber: string, bankNumber: string): Observable<boolean> {
    if (!ViewConfigurations.isPaystackEnabled) {
      return of(null);
    }
    return this.affiliateProvider.validateAccountNumber(accountNumber,
      bankNumber).pipe(
        switchMap((response) => {
          if (response) {
            return of(true);
          }
          return of(null);
        }),
        publishReplay(1),
        refCount(),
        catchError((err) => {
          // observableThrowError(this.handleError(err));
          console.log(err);
          return [];
        }),
      );
  }

  updateBankData(affiliateId: number, accountNumber: string, externalBankIdentifier: string): Observable<boolean> {
    const bankDataRequest: IBankDataRequest = {
      affiliateId,
      accountNumber,
      externalBankIdentifier
    };
    return this.affiliateProvider.updateBankData(bankDataRequest).pipe(
      switchMap((response) => {
        if (response) {
          // auto login user after finish registration
          // this.authService.login(registrationConfirm.username, registrationConfirm.newPassword).subscribe();
          return of(true);
        }
        return of(false);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        const notification = {
          title: DynamicTranslations.communication_Error_title,
          message: DynamicTranslations.communication_Error_errorMessage,
          errorLevel: ErrorLevel.Warning,
          errorType: ErrorType.ComunicationError,
          link: '/betting/prematch'
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        console.log(err);
        return of(false);
      }),
    );
  }

  getBankDataByAffiliateId(id: number): Observable<IBankDataRequest> {
    if (!ViewConfigurations.isPaystackEnabled) {
      return of(null);
    }
    return this.affiliateProvider.getBankDataByAffiliateId(id).pipe(
      switchMap((response) => {
        if (response) {
          return of(response as IBankDataRequest);
        }
        const notification = {
          title: DynamicTranslations.communication_Error_title,
          message: DynamicTranslations.communication_Error_errorMessage,
          errorLevel: ErrorLevel.Fatal,
          errorType: ErrorType.Fatal,
          link: '/betting/prematch'
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getUnregisteredAffiliatesByParentId(id: number): Observable<IUnregisteredAffiliateResponse[]> {
    this.loaderService.showHideLoader(true);
    return this.affiliateProvider.getUnregisteredAffiliatesByParentId(id).pipe(
      switchMap((response) => {
        this.loaderService.showHideLoader(false);
        if (response) {
          return of(response as IUnregisteredAffiliateResponse[]);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        this.loaderService.showHideLoader(false);
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  transferBalanceAmount(amount: number, toId: number, transactionType: TransactionType): Observable<boolean> {
    if (!ViewConfigurations.isPaystackEnabled) {
      return of(true);
    }
    const transferBalanceAmount: ITransferBalanceAmountRequest = {
      targetAffiliateId: toId,
      transactionType,
      amount
    };

    return this.affiliateProvider.transferBalanceAmount(transferBalanceAmount).pipe(
      switchMap((balanceResponse) => {
        if (balanceResponse) {
          balanceResponse.newBalances.forEach(balance => {
            if (this.authService.loggedAffiliate.affiliateId === balance.affiliateId) {
              this.authService.loggedAffiliate.currentBalance = balance.amount;
              this.storageService.accountBalance = balance.amount;
              this.storageService.affiliate = this.authService.loggedAffiliate;
            } else {
              const aff = this.loggedAffiliateSubAffiliates?.find(aff => aff.affiliateId === balance.affiliateId);
              if (aff) {
                aff.currentBalance = balance.amount;
              }
            }
          });
          return of(true);
        }
        return of(false);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getSessionsPerPage(dateFrom: Date, dateTo: Date, affiliateId: number, pageNumber: number, count: number): Observable<IAffiliateSessionsWithPages> {
    return this.affiliateProvider.getPaginatedSessions(dateFrom, dateTo, affiliateId, (pageNumber - 1) * count, count).pipe(
      map((sessionsAndCount: { totalCount: number; affiliateSessions: IAffiliateSessionTO[] }) => ({
        totalPages: sessionsAndCount.totalCount ? Math.ceil((sessionsAndCount.totalCount / count)) : 0,
        affiliateSessions: sessionsAndCount.affiliateSessions ? sessionsAndCount.affiliateSessions.map((sessionTO) => this.modelMapper.mapAffiliateSession(sessionTO)) : []
      })
      )
    );
  }
  checkResetPasswordToken(token: string): Observable<boolean> {
    return this.affiliateProvider.checkResetPasswordToken(token);
  }

  resetPassword(id: number): Observable<any> {
    return this.affiliateProvider.resetPassword(id).pipe(
      switchMap((response) => {
        if (response) {
          const notification = {
            title: DynamicTranslations.notification_title,
            message: DynamicTranslations.ticketManager_RegistrationEmailSent,
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          return of(true);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  setNewAffiliatePassword(token: string, password: string): Observable<any> {
    const setNewAffiliatePasswordRequest: any = {
      token,
      newPassword: password,
    };
    return this.affiliateProvider.setNewAffiliatePassword(setNewAffiliatePasswordRequest).pipe(
      switchMap((response) => {
        if (response) {
          return of(true);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  changeAffiliatePassword(oldpassword: string, newPassword: string): Observable<any> {
    const changeAffiliatePasswordRequest: any = {
      oldpassword,
      newPassword
    };
    return this.affiliateProvider.changeAffiliatePassword(changeAffiliatePasswordRequest).pipe(
      switchMap((response) => {
        if (response.isSuccess) {
          const notification = {
            title: DynamicTranslations.notification_title,
            message: DynamicTranslations.ticketManager_PasswordChanged,
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          return of(true);
        }
        const notification = {
          title: DynamicTranslations.notification_title,
          message: response.failureReason,
          errorLevel: ErrorLevel.Warning,
          errorType: ErrorType.Warning,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }


  changeAffiliateCashierPassword(affiliateId: number, newPassword: string): Observable<any> {
    const changeAffiliatePasswordRequest: any = {
      affiliateId,
      newPassword
    };
    return this.affiliateProvider.changeAffiliateCashierPassword(changeAffiliatePasswordRequest).pipe(
      switchMap((response) => {
        if (response.isSuccess) {
          const notification = {
            title: DynamicTranslations.notification_title,
            message: DynamicTranslations.ticketManager_PasswordChanged,
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          return of(true);
        }
        const notification = {
          title: DynamicTranslations.notification_title,
          message: response.failureReason,
          errorLevel: ErrorLevel.Warning,
          errorType: ErrorType.Warning,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  resendRegistrationEmail(idAffiliate: number) {
    return this.affiliateProvider.resendRegistrationEmail(idAffiliate).pipe(
      switchMap((response) => {
        if (response) {
          const notification = {
            title: DynamicTranslations.notification_title,
            message: DynamicTranslations.ticketManager_RegistrationEmailSent,
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          return of(true);
        }
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.communication_Error_errorMessage,
          errorLevel: ErrorLevel.Notification,
          errorType: ErrorType.Fatal,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  deleteAffiliate(affiliateId: number, worksInShopId: number): Observable<void> {
    return this.affiliateProvider.deleteAffiliate(affiliateId).pipe(
      switchMap(() => {
        let affiliate: IAffiliate;
        if (worksInShopId) {
          affiliate = this.affiliatesByShopId[worksInShopId]?.find(aff => aff.affiliateId === affiliateId);
        } else {
          affiliate = this.loggedAffiliateSubAffiliates?.find(aff => aff.affiliateId === affiliateId);
        }

        if (affiliate) {
          affiliate.isDeleted = true;
        }
        this.loggedAffiliateSubAffiliates = this.loggedAffiliateSubAffiliates.filter(s => s.affiliateId !== affiliateId);
        this.loggedAffiliateSubAffiliatesChangedSource.next();

        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_Affiliate + ' - ' + DynamicTranslations.notification_deleted,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  deleteRegistrationAffiliate(affiliateId: number): Observable<void> {
    return this.affiliateProvider.deleteRegistrationAffiliate(affiliateId).pipe(
      switchMap(() => {
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_RegistrationRequest + ' - ' + DynamicTranslations.notification_deleted,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  updateRegistrationRequestEmail(registrationId: number, newEmailAddres: string): Observable<boolean> {
    return this.affiliateProvider.updateRegistrationRequestEmail(registrationId, newEmailAddres).pipe(
      switchMap((isSuccess) => {
        if (isSuccess) {
          const notification = {
            title: DynamicTranslations.notification_title,
            message: DynamicTranslations.ticketManager_EmailChanged,
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          return of(true);
        }
        return of(false);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  deposit(amount: number): Observable<string> {
    if (!ViewConfigurations.isPaystackEnabled) {
      return of(null);
    }
    return this.affiliateProvider.deposit(this.authService.loggedAffiliate.affiliateId, amount).pipe(
      switchMap(response => {
        if (response) {
          return of(response.authorizationUrl);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  withdraw(amount: number): Observable<boolean> {
    if (!ViewConfigurations.isPaystackEnabled) {
      return of(true);
    }
    return this.affiliateProvider.withdraw(this.authService.loggedAffiliate.affiliateId, amount).pipe(
      switchMap(response => {
        if (response) {
          const notification = {
            title: DynamicTranslations.notification_title,
            message: 'Payout request sent, account balance will be changed soon.',
            errorLevel: ErrorLevel.Success,
            errorType: ErrorType.CloseDialog,
          };
          this.notificationService.forwardDataToNotification(notification as ICustomNotification);
          return of(true);
        }
        return of(false);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  createAffiliateSetting(affiliateSetting: IAffiliateSetting): Observable<IAffiliateSetting> {
    return this.affiliateProvider.createAffiliateSetting(this.modelMapper.mapAffiliateSettingDA(affiliateSetting)).pipe(
      switchMap((affiliateSettingDA) => {
        if (!affiliateSettingDA) {
          return of(null);
        }
        const affSetting = this.modelMapper.mapAffiliateSetting(affiliateSettingDA);
        if (this.affiliateSettings) {
          const bonusSettingIndex = this.affiliateSettings.findIndex(s => s.id === affSetting.id);
          if (bonusSettingIndex < 0) {
            this.affiliateSettings.push(affSetting);
          }
        }
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_AffiliateSetting + ' - ' + DynamicTranslations.notification_created,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getAllAffiliateSettings(): Observable<IAffiliateSetting[]> {

    // if (this.allCommissionsForSuperAgent) {
    //   return of(this.allCommissionsForSuperAgent);
    // }

    // if (this.authService.loggedAffiliate.affiliateRole === AffiliateRole.cashier || this.authService.loggedAffiliate.affiliateRole === AffiliateRole.agent) {
    //   return of(null);
    // }

    return this.affiliateProvider.getAllAffiliateSettings().pipe(
      switchMap((affiliateSettingsListTO) => {
        if (affiliateSettingsListTO) {
          const affiliateSettings = affiliateSettingsListTO.map(affiliateSetting => this.modelMapper.mapAffiliateSetting(affiliateSetting));
          // this.allCommissionsForSuperAgent = commissionSettings;
          // return of(this.allCommissionsForSuperAgent);
          this.affiliateSettings = affiliateSettings;
          return of(affiliateSettings);
        }
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  getAffiliateSettingById(affiliateSettingId: number): Observable<IAffiliateSetting> {
    // if (this.loggedAffiliateId !== this.authService.loggedAffiliate.affiliateId) {
    //   this.bonusSettings = null;
    //   this.loggedAffiliateId = this.authService.loggedAffiliate.affiliateId;
    // }

    if (this.affiliateSettings) {
      const affiliateSetting = this.affiliateSettings.find(bs => bs.id === affiliateSettingId);
      if (affiliateSetting) {
        return of(affiliateSetting);
      }
    }

    return this.affiliateProvider.getAffiliateSettingItemsById(affiliateSettingId).pipe(
      switchMap((affiliateSettingDA) => of(this.modelMapper.mapAffiliateSetting(affiliateSettingDA))),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );

  }

  updateAffiliateSetting(affiliateSetting: IAffiliateSetting): Observable<IAffiliateSetting> {
    return this.affiliateProvider.updateAffiliateSetting(this.modelMapper.mapAffiliateSettingDA(affiliateSetting)).pipe(
      switchMap((affiliateSettingDA) => {
        if (!affiliateSettingDA) {
          return of(null);
        }
        const affiliateSetting = this.modelMapper.mapAffiliateSetting(affiliateSettingDA);
        if (this.affiliateSettings) {
          const affiliateSettingIndex = this.affiliateSettings.findIndex(s => s.id === affiliateSetting.id);
          if (affiliateSettingIndex > -1) {
            this.affiliateSettings[affiliateSettingIndex] = affiliateSetting;
          }
        }
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_AffiliateSetting + ' - ' + DynamicTranslations.notification_updated,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(affiliateSetting);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  deleteAffiliateSetting(affiliateSettingId: number): Observable<void> {
    return this.affiliateProvider.deleteAffiliateSetting(affiliateSettingId).pipe(
      switchMap((isDeleted) => {
        if (!isDeleted) {
          return of(null);
        }

        if (this.affiliateSettings) {
          const bonusSettingIndex = this.affiliateSettings.findIndex(s => s.id === affiliateSettingId);
          if (bonusSettingIndex > -1) {
            this.affiliateSettings.splice(bonusSettingIndex, 1);
          }
        }
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.ticketManager_AffiliateSetting + ' - ' + DynamicTranslations.notification_deleted,
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(null);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }


  addToInitialBalance(amount: number): Observable<number> {
    return this.affiliateProvider.addToInitialBalance(amount).pipe(
      switchMap((newBalance) => {
        if (!newBalance) {
          return of(null);
        }

        const notification = {
          title: DynamicTranslations.notification_title,
          message: 'Added to balance.',
          errorLevel: ErrorLevel.Success,
          errorType: ErrorType.CloseDialog,
        };
        this.notificationService.forwardDataToNotification(notification as ICustomNotification);
        return of(newBalance);
      }),
      publishReplay(1),
      refCount(),
      catchError((err) => {
        // observableThrowError(this.handleError(err));
        console.log(err);
        return [];
      }),
    );
  }

  // validateBankAccount(form: any, token: string, accountNumber: string, bankNumber: string): Observable<boolean> {
  //   return this.affiliateProvider.validateAccountNumber(accountNumber,
  //     bankNumber).pipe(
  //     switchMap((response) => {
  //       if (response) {
  //         this.confirmRegistration(form, token, accountNumber, bankNumber).subscribe(response => {
  //           if (response) {
  //             return of(true);
  //           } else {
  //             return of(null);
  //           }
  //         });
  //       }
  //       return of(null);
  //     }),
  //     publishReplay(1),
  //     refCount(),
  //     catchError((err) => {
  //       // observableThrowError(this.handleError(err));
  //       console.log(err);
  //       return [];
  //     }),
  //   );
  // }
  // createLogin(affiliateId: number, username: string, password: string): Observable<boolean> {
  //   return this.affiliateProvider.createLogin(affiliateId, username, password).pipe(
  //     switchMap((response) => {
  //       if (response) {
  //         return of(true);
  //       }
  //       return of(false);
  //     }),
  //     publishReplay(1),
  //     refCount(),
  //     catchError((err) => {
  //       // observableThrowError(this.handleError(err));
  //       console.log(err);
  //       return [];
  //     }),
  //   );
  // }
}
